import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { BookOpen, Camera, CheckCircle2, Package, Plus, UserCheck } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { api, del, get, post } from '../lib/api.ts'
import { fmtDate, fmtDateTime } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import { Badge, Button, cn, Dialog, Empty, ErrorNote, Field, Input, PageHeader, Select, Sheet, Spinner, Textarea } from '../components/ui.tsx'
import { t } from '../lib/i18n.ts'

export interface Unit { id: string; label: string; block: string | null; mine: boolean }
interface Delivery { id: string; unit: { id: string; label: string }; description: string; carrier: string | null; hasPhoto: boolean; notes?: string | null; receivedAt: string; receivedBy: string | null; notifiedAt: string | null; pickedUpAt: string | null; pickedUpByName: string | null }
interface Visitor { id: string; visitorName: string; kind: string; company: string | null; vehiclePlate: string | null; document?: string | null; validFrom: string; validTo: string; note: string | null; status: string; arrivedAt: string | null; unit: { id: string; label: string } | null; createdBy: string | null; mine: boolean }
interface Vehicle { id: string; unitId: string; unitLabel: string; plate: string; model: string | null; color: string | null; notes: string | null }
interface Pet { id: string; unitId: string; unitLabel: string; name: string; species: string; breed: string | null; notes: string | null }

const KIND_LABEL: Record<string, string> = { visitante: t('Visitante'), prestador: t('Prestador de serviço'), entregador: t('Entregador'), mudanca: t('Mudança') }
const SPECIES_LABEL: Record<string, string> = { cachorro: t('Cachorro'), gato: t('Gato'), ave: t('Ave'), outro: t('Outro') }
const STATUS_TONE: Record<string, 'alerta' | 'carimbo' | 'margem' | 'neutral'> = { prevista: 'alerta', chegou: 'carimbo', cancelada: 'neutral' }
const unitName = (u: Unit) => (u.block ? `${u.block} — ${u.label}` : u.label)
/** Today in the condo's zone (every condo so far is in Brazil; the server re-checks dates anyway). */
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())
const plusDays = (date: string, n: number) => new Date(Date.parse(`${date}T00:00:00Z`) + n * 86400_000).toISOString().slice(0, 10)
export const useUnits = (condoId: string) => useQuery({ queryKey: ['units', condoId], queryFn: () => get<{ units: Unit[] }>(`/api/condos/${condoId}/units`), enabled: Boolean(condoId), staleTime: 60_000 })

type Tab = 'encomendas' | 'visitantes' | 'registro' | 'ocorrencias'
const TABS: [Tab, string][] = [['encomendas', t('Encomendas')], ['visitantes', t('Visitantes')], ['registro', t('Veículos e pets')], ['ocorrencias', t('Ocorrências')]]

/** Portaria: the three things every condo app does without hardware. Staff (portaria.manage) run it; residents see their units. */
export function Portaria() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, can, info } = useCondo(slug)
  const [tab, setTab] = useState<Tab>('encomendas')
  const staff = can('portaria.manage')
  const board = ['sindico', 'subsindico', 'conselheiro'].includes(info.data?.me.role ?? '')
  return (
    <div>
      <PageHeader title={t('Portaria')} />
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-pauta" role="tablist">
        {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cn('-mb-px shrink-0 border-b-2 px-3 py-2 font-semibold', tab === k ? 'border-caneta text-caneta' : 'border-transparent text-tinta-2')}>{l}</button>)}
      </div>
      {tab === 'encomendas' && <Deliveries condoId={condoId} staff={staff} />}
      {tab === 'visitantes' && <Visitors condoId={condoId} staff={staff} canAuthorize={can('visitors.authorize')} />}
      {tab === 'registro' && <RegistrySection condoId={condoId} staff={staff} />}
      {tab === 'ocorrencias' && <ShiftLog condoId={condoId} staff={staff} board={board} />}
    </div>
  )
}

/** Unit picker: staff choose any unit; residents choose among theirs (hidden when they have exactly one: the server picks it). */
export function UnitSelect({ units, staff, value, onChange, id, optional }: { units: Unit[]; staff: boolean; value: string; onChange: (v: string) => void; id: string; optional?: boolean }) {
  const opts = staff ? units : units.filter((u) => u.mine)
  if (!staff && opts.length <= 1) return null
  return (
    <Field label={optional ? t('Unidade (opcional)') : t('Unidade')} htmlFor={id}>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} required={!optional}>
        <option value="">{staff ? (optional ? t('Nenhuma (área comum)') : t('Escolha a unidade')) : t('Escolha uma das suas unidades')}</option>
        {opts.map((u) => <option key={u.id} value={u.id}>{unitName(u)}</option>)}
      </Select>
    </Field>
  )
}

const Actions = ({ onClose, busy, label }: { onClose: () => void; busy: boolean; label: string }) => (
  <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button><Button type="submit" busy={busy}>{label}</Button></div>
)

// ======================= Encomendas =======================
function Deliveries({ condoId, staff }: { condoId: string; staff: boolean }) {
  const qc = useQueryClient()
  const [showAll, setShowAll] = useState(false)
  const [adding, setAdding] = useState(false)
  const [pickup, setPickup] = useState<Delivery | null>(null)
  const [photo, setPhoto] = useState<Delivery | null>(null)
  const status = showAll ? 'todas' : 'pendentes'
  const q = useQuery({ queryKey: ['deliveries', condoId, status], queryFn: () => get<{ deliveries: Delivery[] }>(`/api/condos/${condoId}/portaria/deliveries?status=${status}`), enabled: Boolean(condoId) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['deliveries', condoId] })
  const list = q.data?.deliveries ?? []
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {staff && <Button onClick={() => setAdding(true)}><Plus className="size-5" />{' '}{t('Registrar encomenda')}</Button>}
        <label className="ml-auto flex items-center gap-2 text-[0.95rem]"><input type="checkbox" className="size-4" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />{' '}{t('Mostrar retiradas')}</label>
      </div>
      {q.isLoading ? <Spinner /> : list.length === 0 ? (
        <Empty title={showAll ? t('Nenhuma encomenda registrada') : t('Nenhuma encomenda aguardando retirada')}>
          {staff ? t('Quando chegar um pacote, registre aqui: as pessoas da unidade recebem um aviso no WhatsApp e a retirada fica anotada.') : t('Quando a portaria registrar um pacote para a sua unidade, ele aparece aqui e você recebe um aviso no WhatsApp (se tiver ativado os avisos).')}
        </Empty>
      ) : (
        <Sheet>
          {list.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
              <Package className={cn('size-5 shrink-0', d.pickedUpAt ? 'text-tinta-3' : 'text-caneta')} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{d.unit.label}: {d.description}{d.carrier ? ` (${d.carrier})` : ''}</p>
                <p className="text-sm text-tinta-2">
                  {t('Recebida')}{' '}{fmtDateTime(d.receivedAt)}{d.receivedBy ? t(' por {name}', { name: d.receivedBy }) : ''}{d.notifiedAt ? t(', unidade avisada') : ''}
                  {d.pickedUpAt ? `${t('. Retirada {when}', { when: fmtDateTime(d.pickedUpAt) })}${d.pickedUpByName ? t(' por {name}', { name: d.pickedUpByName }) : ''}` : ''}
                </p>
                {d.notes && <p className="text-sm text-tinta-2">{d.notes}</p>}
              </div>
              {d.hasPhoto && <Button size="sm" variant="quiet" onClick={() => setPhoto(d)}><Camera className="size-4" />{' '}{t('Foto')}</Button>}
              {d.pickedUpAt ? <Badge tone="carimbo">{t('retirada')}</Badge> : <Button size="sm" variant="ok" onClick={() => setPickup(d)}><CheckCircle2 className="size-4" /> {staff ? t('Entregar') : t('Já retirei')}</Button>}
            </div>
          ))}
        </Sheet>
      )}
      {adding && <DeliveryDialog condoId={condoId} onClose={() => setAdding(false)} onDone={invalidate} />}
      {pickup && <PickupDialog condoId={condoId} delivery={pickup} staff={staff} onClose={() => setPickup(null)} onDone={invalidate} />}
      <Dialog open={photo !== null} onClose={() => setPhoto(null)} title={t('Foto da encomenda')}>
        {photo && <img src={`/api/condos/${condoId}/portaria/deliveries/${photo.id}/photo`} alt={t('Encomenda da unidade {unit}', { unit: photo.unit.label })} className="max-h-[70vh] w-full rounded-ficha object-contain" />}
      </Dialog>
    </section>
  )
}

function DeliveryDialog({ condoId, onClose, onDone }: { condoId: string; onClose: () => void; onDone: () => void }) {
  const units = useUnits(condoId)
  const [f, setF] = useState({ unitId: '', description: '', carrier: '', notes: '', notify: true })
  const [file, setFile] = useState<File | null>(null)
  const m = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      fd.set('unitId', f.unitId)
      fd.set('description', f.description)
      if (f.carrier.trim()) fd.set('carrier', f.carrier.trim())
      if (f.notes.trim()) fd.set('notes', f.notes.trim())
      fd.set('notify', f.notify ? '1' : '0')
      if (file) fd.set('file', file)
      return post<{ id: string; notified: number }>(`/api/condos/${condoId}/portaria/deliveries`, fd)
    },
    onSuccess: (r) => { toast.success(r.notified ? t('Encomenda registrada, {n} pessoa(s) avisada(s) no WhatsApp', { n: r.notified }) : t('Encomenda registrada')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Registrar encomenda')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <UnitSelect id="dl-u" units={units.data?.units ?? []} staff value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} />
        <Field label={t('O que chegou')} htmlFor="dl-d"><Input id="dl-d" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required minLength={2} maxLength={200} placeholder={t('Ex.: caixa média, envelope dos Correios')} /></Field>
        <Field label={t('Transportadora (opcional)')} htmlFor="dl-c"><Input id="dl-c" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} maxLength={80} placeholder={t('Correios, Mercado Livre, Amazon…')} /></Field>
        <Field label={t('Foto (opcional)')} htmlFor="dl-f" hint={t('Uma foto da etiqueta ajuda a identificar o pacote.')}><Input id="dl-f" type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
        <Field label={t('Observação interna (opcional)')} htmlFor="dl-n" hint={t('Só a equipe vê.')}><Textarea id="dl-n" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={500} /></Field>
        <label className="flex items-center gap-2 text-[0.95rem]"><input type="checkbox" className="size-4" checked={f.notify} onChange={(e) => setF({ ...f, notify: e.target.checked })} />{' '}{t('Avisar as pessoas da unidade no WhatsApp')}</label>
        <ErrorNote error={m.error} />
        <Actions onClose={onClose} busy={m.isPending} label={t('Registrar')} />
      </form>
    </Dialog>
  )
}

function PickupDialog({ condoId, delivery, staff, onClose, onDone }: { condoId: string; delivery: Delivery; staff: boolean; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('')
  const m = useMutation({
    mutationFn: () => post(`/api/condos/${condoId}/portaria/deliveries/${delivery.id}/pickup`, name.trim() ? { pickedUpByName: name.trim() } : {}),
    onSuccess: () => { toast.success(t('Retirada registrada')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={staff ? t('Registrar retirada') : t('Confirmar retirada')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <p className="text-[0.95rem] text-tinta-2">{delivery.unit.label}: {delivery.description}{delivery.carrier ? ` (${delivery.carrier})` : ''}</p>
        {staff && <Field label={t('Quem retirou (opcional)')} htmlFor="pk-n" hint={t('Em branco, fica registrado o seu nome.')}><Input id="pk-n" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></Field>}
        <ErrorNote error={m.error} />
        <Actions onClose={onClose} busy={m.isPending} label={staff ? t('Registrar retirada') : t('Já retirei')} />
      </form>
    </Dialog>
  )
}

// ======================= Visitantes =======================
function Visitors({ condoId, staff, canAuthorize }: { condoId: string; staff: boolean; canAuthorize: boolean }) {
  const qc = useQueryClient()
  const [from, setFrom] = useState(today())
  const [adding, setAdding] = useState(false)
  const to = plusDays(from, 7)
  const q = useQuery({ queryKey: ['visitors', condoId, from], queryFn: () => get<{ visitors: Visitor[] }>(`/api/condos/${condoId}/portaria/visitors?from=${from}&to=${to}`), enabled: Boolean(condoId) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['visitors', condoId] })
  const arrived = useMutation({ mutationFn: (id: string) => post(`/api/condos/${condoId}/portaria/visitors/${id}/arrived`), onSuccess: () => { toast.success(t('Chegada registrada')); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const cancel = useMutation({ mutationFn: (id: string) => post(`/api/condos/${condoId}/portaria/visitors/${id}/cancel`), onSuccess: () => { toast.success(t('Autorização cancelada')); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const list = q.data?.visitors ?? []
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {canAuthorize && <Button onClick={() => setAdding(true)}><Plus className="size-5" />{' '}{t('Autorizar entrada')}</Button>}
        <label className="ml-auto flex items-center gap-2 text-[0.95rem]">{t('Semana a partir de')}{' '}<Input type="date" className="w-auto" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
      </div>
      {q.isLoading ? <Spinner /> : list.length === 0 ? (
        <Empty title={t('Ninguém previsto neste período')}>
          {canAuthorize ? t('Avise a portaria com antecedência: autorize aqui visitantes, prestadores de serviço, entregadores e mudanças. A equipe vê a lista do dia e registra a chegada.') : t('As autorizações feitas pelos moradores aparecem aqui.')}
        </Empty>
      ) : (
        <Sheet>
          {list.map((v) => (
            <div key={v.id} className={cn('flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0', v.status === 'cancelada' && 'opacity-60')}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{v.visitorName}{v.company ? `, ${v.company}` : ''} <span className="font-normal text-tinta-2">({KIND_LABEL[v.kind] ?? v.kind})</span></p>
                <p className="text-sm text-tinta-2">
                  {v.validFrom === v.validTo ? fmtDate(v.validFrom) : `${fmtDate(v.validFrom)} a ${fmtDate(v.validTo)}`}
                  {v.unit ? t(', unidade {unit}', { unit: v.unit.label }) : ''}{v.vehiclePlate ? t(', placa {plate}', { plate: v.vehiclePlate }) : ''}{v.document ? t(', documento {doc}', { doc: v.document }) : ''}{v.createdBy && !v.mine ? t(', autorizado por {name}', { name: v.createdBy }) : ''}
                </p>
                {v.note && <p className="text-sm text-tinta-2">{v.note}</p>}
                {v.arrivedAt && <p className="text-sm text-tinta-2">{t('Chegou')}{' '}{fmtDateTime(v.arrivedAt)}</p>}
              </div>
              <Badge tone={STATUS_TONE[v.status] ?? 'neutral'}>{v.status}</Badge>
              {staff && v.status === 'prevista' && <Button size="sm" variant="ok" onClick={() => arrived.mutate(v.id)}><UserCheck className="size-4" />{' '}{t('Chegou')}</Button>}
              {(v.mine || staff) && v.status === 'prevista' && <Button size="sm" variant="quiet" onClick={() => confirm(t('Cancelar esta autorização?')) && cancel.mutate(v.id)}>{t('Cancelar')}</Button>}
            </div>
          ))}
        </Sheet>
      )}
      {adding && <VisitorDialog condoId={condoId} staff={staff} onClose={() => setAdding(false)} onDone={invalidate} />}
    </section>
  )
}

function VisitorDialog({ condoId, staff, onClose, onDone }: { condoId: string; staff: boolean; onClose: () => void; onDone: () => void }) {
  const units = useUnits(condoId)
  const d = today()
  const [f, setF] = useState({ visitorName: '', kind: 'visitante', company: '', document: '', vehiclePlate: '', validFrom: d, validTo: d, note: '', unitId: '' })
  const m = useMutation({
    mutationFn: () => post<{ id: string }>(`/api/condos/${condoId}/portaria/visitors`, {
      visitorName: f.visitorName.trim(), kind: f.kind, company: f.company.trim() || null, document: f.document.trim() || null, vehiclePlate: f.vehiclePlate.trim() || null,
      validFrom: f.validFrom, validTo: f.validTo || f.validFrom, note: f.note.trim() || null, unitId: f.unitId || null,
    }),
    onSuccess: () => { toast.success(t('Entrada autorizada, a portaria já vê')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Autorizar entrada')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <Field label={t('Nome')} htmlFor="vi-n"><Input id="vi-n" value={f.visitorName} onChange={(e) => setF({ ...f, visitorName: e.target.value })} required minLength={2} maxLength={120} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Tipo')} htmlFor="vi-k"><Select id="vi-k" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
          <Field label={t('Empresa (opcional)')} htmlFor="vi-c"><Input id="vi-c" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} maxLength={120} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('De')} htmlFor="vi-f"><Input id="vi-f" type="date" value={f.validFrom} onChange={(e) => setF({ ...f, validFrom: e.target.value, validTo: f.validTo < e.target.value ? e.target.value : f.validTo })} required /></Field>
          <Field label={t('Até')} htmlFor="vi-t" hint={t('No máximo 90 dias.')}><Input id="vi-t" type="date" value={f.validTo} min={f.validFrom} onChange={(e) => setF({ ...f, validTo: e.target.value })} required /></Field>
        </div>
        <UnitSelect id="vi-u" units={units.data?.units ?? []} staff={staff} value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} optional={staff} />
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Documento (opcional)')} htmlFor="vi-d" hint={t('RG ou CPF, só a portaria vê.')}><Input id="vi-d" value={f.document} onChange={(e) => setF({ ...f, document: e.target.value })} maxLength={40} /></Field>
          <Field label={t('Placa (opcional)')} htmlFor="vi-p"><Input id="vi-p" value={f.vehiclePlate} onChange={(e) => setF({ ...f, vehiclePlate: e.target.value.toUpperCase() })} maxLength={10} placeholder={t('ABC1D23')} /></Field>
        </div>
        <Field label={t('Recado para a portaria (opcional)')} htmlFor="vi-o"><Textarea id="vi-o" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={500} placeholder={t('Ex.: vai trocar o chuveiro, pode liberar o elevador de serviço')} /></Field>
        <ErrorNote error={m.error} />
        <Actions onClose={onClose} busy={m.isPending} label={t('Autorizar')} />
      </form>
    </Dialog>
  )
}

// ======================= Veículos e pets =======================
function RegistrySection({ condoId, staff }: { condoId: string; staff: boolean }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState<'vehicle' | 'pet' | null>(null)
  const [plate, setPlate] = useState('')
  const [search, setSearch] = useState('')
  const q = useQuery({ queryKey: ['registry', condoId], queryFn: () => get<{ vehicles: Vehicle[]; pets: Pet[] }>(`/api/condos/${condoId}/portaria/registry`), enabled: Boolean(condoId) })
  const found = useQuery({ queryKey: ['plate', condoId, search], queryFn: () => get<{ vehicles: Vehicle[] }>(`/api/condos/${condoId}/portaria/vehicles/search?plate=${encodeURIComponent(search)}`), enabled: Boolean(condoId && staff && search.length >= 3) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['registry', condoId] })
  const rmVehicle = useMutation({ mutationFn: (id: string) => del(`/api/condos/${condoId}/portaria/vehicles/${id}`), onSuccess: () => { toast.success(t('Veículo removido')); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const rmPet = useMutation({ mutationFn: (id: string) => del(`/api/condos/${condoId}/portaria/pets/${id}`), onSuccess: () => { toast.success(t('Pet removido')); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const vehicles = q.data?.vehicles ?? []
  const pets = q.data?.pets ?? []
  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setAdding('vehicle')}><Plus className="size-5" />{' '}{t('Veículo')}</Button>
        <Button variant="outline" onClick={() => setAdding('pet')}><Plus className="size-5" />{' '}{t('Pet')}</Button>
        {staff && (
          <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setSearch(plate.toUpperCase().replace(/[^A-Z0-9]/g, '')) }}>
            <Input aria-label={t('Buscar placa')} value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder={t('De quem é a placa…')} maxLength={10} className="w-44" />
            <Button type="submit" variant="outline" size="sm">{t('Buscar')}</Button>
          </form>
        )}
      </div>
      {staff && search.length >= 3 && (
        <Sheet className="p-4">
          <p className="mb-1 font-bold">{t('Placas com “')}{search}”</p>
          {found.isLoading ? <Spinner /> : found.data?.vehicles.length ? found.data.vehicles.map((v) => <p key={v.id}><span className="font-semibold">{v.plate}</span> {[v.model, v.color].filter(Boolean).join(' ')}{t(': unidade')}{' '}{v.unitLabel}</p>) : <p className="text-tinta-2">{t('Nenhum veículo cadastrado com essa placa.')}</p>}
        </Sheet>
      )}
      {q.isLoading ? <Spinner /> : (
        <>
          <div>
            <h2 className="mb-2 text-xl font-bold">{t('Veículos')}</h2>
            {vehicles.length === 0 ? <p className="text-tinta-2">{staff ? t('Nenhum veículo cadastrado ainda.') : t('Cadastre os veículos da sua unidade: a portaria sabe de quem é o carro na vaga errada sem precisar perguntar.')}</p> : (
              <Sheet>
                {vehicles.map((v) => (
                  <div key={v.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
                    <div className="min-w-0 flex-1"><p className="font-semibold">{v.plate} <span className="font-normal text-tinta-2">{[v.model, v.color].filter(Boolean).join(', ')}</span></p><p className="text-sm text-tinta-2">{t('Unidade')}{' '}{v.unitLabel}{v.notes ? `, ${v.notes}` : ''}</p></div>
                    <Button size="sm" variant="quiet" onClick={() => confirm(t('Remover o veículo {plate}?', { plate: v.plate })) && rmVehicle.mutate(v.id)}>{t('Remover')}</Button>
                  </div>
                ))}
              </Sheet>
            )}
          </div>
          <div>
            <h2 className="mb-2 text-xl font-bold">{t('Pets')}</h2>
            {pets.length === 0 ? <p className="text-tinta-2">{staff ? t('Nenhum pet cadastrado ainda.') : t('Cadastre os pets da sua unidade: ajuda a portaria quando um escapa e evita discussões sobre as regras do regimento.')}</p> : (
              <Sheet>
                {pets.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
                    <div className="min-w-0 flex-1"><p className="font-semibold">{p.name} <span className="font-normal text-tinta-2">{[SPECIES_LABEL[p.species] ?? p.species, p.breed].filter(Boolean).join(', ')}</span></p><p className="text-sm text-tinta-2">{t('Unidade')}{' '}{p.unitLabel}{p.notes ? `, ${p.notes}` : ''}</p></div>
                    <Button size="sm" variant="quiet" onClick={() => confirm(t('Remover {name} do cadastro?', { name: p.name })) && rmPet.mutate(p.id)}>{t('Remover')}</Button>
                  </div>
                ))}
              </Sheet>
            )}
          </div>
        </>
      )}
      {adding === 'vehicle' && <VehicleDialog condoId={condoId} staff={staff} onClose={() => setAdding(null)} onDone={invalidate} />}
      {adding === 'pet' && <PetDialog condoId={condoId} staff={staff} onClose={() => setAdding(null)} onDone={invalidate} />}
    </section>
  )
}

function VehicleDialog({ condoId, staff, onClose, onDone }: { condoId: string; staff: boolean; onClose: () => void; onDone: () => void }) {
  const units = useUnits(condoId)
  const [f, setF] = useState({ unitId: '', plate: '', model: '', color: '', notes: '' })
  const m = useMutation({
    mutationFn: () => post<{ id: string }>(`/api/condos/${condoId}/portaria/vehicles`, { unitId: f.unitId || null, plate: f.plate, model: f.model.trim() || null, color: f.color.trim() || null, notes: f.notes.trim() || null }),
    onSuccess: () => { toast.success(t('Veículo cadastrado')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Cadastrar veículo')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <UnitSelect id="ve-u" units={units.data?.units ?? []} staff={staff} value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} />
        <Field label={t('Placa')} htmlFor="ve-p"><Input id="ve-p" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} required maxLength={10} placeholder={t('ABC1D23')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Modelo (opcional)')} htmlFor="ve-m"><Input id="ve-m" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} maxLength={80} placeholder={t('Fiat Argo')} /></Field>
          <Field label={t('Cor (opcional)')} htmlFor="ve-c"><Input id="ve-c" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} maxLength={40} /></Field>
        </div>
        <Field label={t('Observação (opcional)')} htmlFor="ve-n"><Input id="ve-n" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={200} placeholder={t('Ex.: vaga 12, moto do filho')} /></Field>
        <ErrorNote error={m.error} />
        <Actions onClose={onClose} busy={m.isPending} label={t('Cadastrar')} />
      </form>
    </Dialog>
  )
}

function PetDialog({ condoId, staff, onClose, onDone }: { condoId: string; staff: boolean; onClose: () => void; onDone: () => void }) {
  const units = useUnits(condoId)
  const [f, setF] = useState({ unitId: '', name: '', species: 'cachorro', breed: '', notes: '' })
  const m = useMutation({
    mutationFn: () => post<{ id: string }>(`/api/condos/${condoId}/portaria/pets`, { unitId: f.unitId || null, name: f.name.trim(), species: f.species, breed: f.breed.trim() || null, notes: f.notes.trim() || null }),
    onSuccess: () => { toast.success(t('Pet cadastrado')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Cadastrar pet')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <UnitSelect id="pe-u" units={units.data?.units ?? []} staff={staff} value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Nome')} htmlFor="pe-n"><Input id="pe-n" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={80} /></Field>
          <Field label={t('Espécie')} htmlFor="pe-s"><Select id="pe-s" value={f.species} onChange={(e) => setF({ ...f, species: e.target.value })}>{Object.entries(SPECIES_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
        </div>
        <Field label={t('Raça (opcional)')} htmlFor="pe-b"><Input id="pe-b" value={f.breed} onChange={(e) => setF({ ...f, breed: e.target.value })} maxLength={80} /></Field>
        <Field label={t('Observação (opcional)')} htmlFor="pe-o"><Input id="pe-o" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={300} placeholder={t('Ex.: porte grande, usa focinheira')} /></Field>
        <ErrorNote error={m.error} />
        <Actions onClose={onClose} busy={m.isPending} label={t('Cadastrar')} />
      </form>
    </Dialog>
  )
}

// ======================= Livro de ocorrências (ADR 0031) =======================
interface LogEntry { id: string; category: string; text: string; unit: { id: string; label: string } | null; hasPhoto: boolean; author: string; occurredAt: string; seenAt: string | null; seenBy: string | null }
const LOG_CATS: [string, string][] = [['ocorrencia', t('Ocorrência')], ['seguranca', t('Segurança')], ['manutencao', t('Manutenção')], ['barulho', t('Barulho')], ['entrega', t('Entrega')], ['outro', t('Outro')]]
const logCatLabel = (v: string) => LOG_CATS.find((x) => x[0] === v)?.[1] ?? v

function ShiftLog({ condoId, staff, board }: { condoId: string; staff: boolean; board: boolean }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [photo, setPhoto] = useState<LogEntry | null>(null)
  const q = useQuery({ queryKey: ['portaria-log', condoId], queryFn: () => get<{ entries: LogEntry[] }>(`/api/condos/${condoId}/portaria/log`), enabled: Boolean(condoId) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['portaria-log', condoId] })
  const seen = useMutation({ mutationFn: (id: string) => post(`/api/condos/${condoId}/portaria/log/${id}/seen`), onSuccess: invalidate })
  const list = q.data?.entries ?? []
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {staff && <Button onClick={() => setAdding(true)}><Plus className="size-5" />{' '}{t('Registrar ocorrência')}</Button>}
        <p className="text-sm text-tinta-2">{staff || board ? t('O livro do turno: o que a portaria viu, com hora, unidade e foto. O síndico dá o visto.') : t('Aqui aparecem as ocorrências que a portaria registrou sobre as suas unidades.')}</p>
      </div>
      {q.isLoading ? <Spinner /> : list.length === 0 ? (
        <Empty title={t('Nenhuma ocorrência registrada')}>{staff ? t('Portão aberto, carro na vaga errada, vazamento visto de madrugada: registre aqui, com foto se ajudar. O síndico vê tudo; os moradores, só o que fala da unidade deles.') : t('Quando a portaria registrar algo sobre a sua unidade, aparece aqui.')}</Empty>
      ) : (
        <Sheet>
          {list.map((e) => (
            <div key={e.id} className="border-b border-pauta px-4 py-3 last:border-b-0" data-testid="log-entry">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={e.category === 'seguranca' ? 'margem' : e.category === 'manutencao' ? 'alerta' : 'neutral'}>{logCatLabel(e.category)}</Badge>
                {e.unit && <span className="text-sm font-semibold">{e.unit.label}</span>}
                <span className="text-sm text-tinta-2">{fmtDateTime(e.occurredAt)} · {e.author}</span>
                {e.seenAt ? <Badge tone="carimbo">{t('visto')}{e.seenBy ? t(' por {name}', { name: e.seenBy.split(' ')[0] }) : ''}</Badge> : board ? <Button size="sm" variant="outline" onClick={() => seen.mutate(e.id)}>{t('Visto')}</Button> : null}
              </div>
              <p className="mt-1 whitespace-pre-wrap">{e.text}</p>
              {e.hasPhoto && <Button size="sm" variant="quiet" className="mt-1" onClick={() => setPhoto(e)}><Camera className="size-4" />{' '}{t('Foto')}</Button>}
            </div>
          ))}
        </Sheet>
      )}
      {adding && <LogDialog condoId={condoId} onClose={() => setAdding(false)} onDone={invalidate} />}
      <Dialog open={photo !== null} onClose={() => setPhoto(null)} title={t('Foto da ocorrência')}>
        {photo && <img src={`/api/condos/${condoId}/portaria/log/${photo.id}/photo`} alt={t('Foto da ocorrência')} className="max-h-[70vh] w-full rounded-ficha object-contain" />}
      </Dialog>
    </section>
  )
}

function LogDialog({ condoId, onClose, onDone }: { condoId: string; onClose: () => void; onDone: () => void }) {
  const units = useUnits(condoId)
  const [f, setF] = useState({ category: 'ocorrencia', text: '', unitId: '' })
  const [file, setFile] = useState<File | null>(null)
  const save = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      fd.set('category', f.category)
      fd.set('text', f.text.trim())
      fd.set('unitId', f.unitId)
      if (file) fd.set('file', file)
      return api('POST', `/api/condos/${condoId}/portaria/log`, fd)
    },
    onSuccess: () => { toast.success(t('Ocorrência registrada')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Registrar ocorrência')}>
      <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); save.mutate() }}>
        <Field label={t('Tipo')} htmlFor="lg-c"><Select id="lg-c" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{LOG_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
        <Field label={t('O que aconteceu')} htmlFor="lg-t"><Textarea id="lg-t" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} maxLength={2000} required placeholder={t('Portão da garagem ficou aberto das 2h às 2h40; fechado pelo porteiro.')} /></Field>
        <UnitSelect units={units.data?.units ?? []} staff value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} id="lg-u" optional />
        <Field label={t('Foto (opcional)')} htmlFor="lg-f"><Input id="lg-f" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
        <ErrorNote error={save.error} />
        <Actions onClose={onClose} busy={save.isPending} label={t('Registrar')} />
      </form>
    </Dialog>
  )
}
